import {
  fetchAdministrationMarketplacePricingSales,
  fetchAdministrationMarketplacePricingSalesSummary,
  fetchAdministrationMarketplaceProductPricingSalesSummary,
  type AdministrationMarketplacePricingSalesQuery,
} from "../../../api/administrationMarketplace";
import { createKeyedResourceLoader } from "../../state/keyedResourceLoader";
import type { MarketplaceAdministrationSlice, MarketplacePricingSalesSlice } from "./types";

const summaryLoader = createKeyedResourceLoader();
const productSummaryLoader = createKeyedResourceLoader();
const pageLoader = createKeyedResourceLoader();

export const pricingSalesKey = (query: AdministrationMarketplacePricingSalesQuery) => JSON.stringify({
  ruleId: query.ruleId,
  query: query.query?.trim() || "",
  statuses: [...(query.statuses || [])].sort(),
  funding: [...(query.funding || [])].sort(),
  dateFrom: query.dateFrom || "",
  dateTo: query.dateTo || "",
  sort: query.sort || "newest",
  limit: query.limit || 50,
});

export const marketplacePricingSalesInitialState = {
  pricingSalesSummaries: {},
  productPricingSalesSummaries: {},
  pricingSalesPages: {},
};

export const resetMarketplacePricingSalesRequests = () => {
  summaryLoader.clear();
  productSummaryLoader.clear();
  pageLoader.clear();
};

export const createMarketplacePricingSalesSlice:
MarketplaceAdministrationSlice<MarketplacePricingSalesSlice> = (set, get) => ({
  ...marketplacePricingSalesInitialState,
  loadPricingSalesSummary: (ruleId, options = {}) => {
    const current = get().pricingSalesSummaries[ruleId];
    return summaryLoader.load({
      key: ruleId,
      force: options.force,
      cached: Boolean(current?.hydrated),
      request: () => fetchAdministrationMarketplacePricingSalesSummary(ruleId),
      onStart: () => set((state) => ({ pricingSalesSummaries: {
        ...state.pricingSalesSummaries,
        [ruleId]: { ...current, hydrated: current?.hydrated || false, loading: true, error: "" },
      } })),
      onSuccess: (data) => set((state) => ({ pricingSalesSummaries: {
        ...state.pricingSalesSummaries,
        [ruleId]: { ...data, hydrated: true, loading: false, error: "" },
      } })),
      onError: (error) => set((state) => ({ pricingSalesSummaries: {
        ...state.pricingSalesSummaries,
        [ruleId]: { ...state.pricingSalesSummaries[ruleId], hydrated: false, loading: false, error },
      } })),
      onFinish: () => undefined,
      errorMessage: "Unable to load pricing-version sales summary.",
    });
  },
  loadProductPricingSalesSummary: (productId, options = {}) => {
    const current = get().productPricingSalesSummaries[productId];
    return productSummaryLoader.load({
      key: productId,
      force: options.force,
      cached: Boolean(current?.hydrated),
      request: () => fetchAdministrationMarketplaceProductPricingSalesSummary(productId),
      onStart: () => set((state) => ({ productPricingSalesSummaries: {
        ...state.productPricingSalesSummaries,
        [productId]: { ...current, hydrated: current?.hydrated || false, loading: true, error: "" },
      } })),
      onSuccess: (data) => set((state) => ({ productPricingSalesSummaries: {
        ...state.productPricingSalesSummaries,
        [productId]: { ...data, hydrated: true, loading: false, error: "" },
      } })),
      onError: (error) => set((state) => ({ productPricingSalesSummaries: {
        ...state.productPricingSalesSummaries,
        [productId]: { ...state.productPricingSalesSummaries[productId], hydrated: false, loading: false, error },
      } })),
      onFinish: () => undefined,
      errorMessage: "Unable to load product pricing sales summary.",
    });
  },
  loadPricingSales: (query, options = {}) => {
    const key = pricingSalesKey(query);
    const current = get().pricingSalesPages[key];
    const append = Boolean(options.append && query.cursor);
    return pageLoader.load({
      key: `${key}:${query.cursor || "first"}`,
      force: options.force,
      cached: !append && Boolean(current?.hydrated),
      request: () => fetchAdministrationMarketplacePricingSales(query),
      onStart: () => set((state) => ({ pricingSalesPages: { ...state.pricingSalesPages, [key]: {
        query: { ...query, cursor: null }, items: current?.items || [], nextCursor: current?.nextCursor || null,
        totalCount: current?.totalCount || 0, hydrated: current?.hydrated || false,
        loading: !append, loadingMore: append, error: "",
      } } })),
      onSuccess: (page) => set((state) => ({ pricingSalesPages: { ...state.pricingSalesPages, [key]: {
        query: { ...query, cursor: null }, ...page,
        items: append
          ? [...(state.pricingSalesPages[key]?.items || []), ...page.items.filter((item) => !(state.pricingSalesPages[key]?.items || []).some((existing) => existing.id === item.id))]
          : page.items,
        hydrated: true, loading: false, loadingMore: false, error: "",
      } } })),
      onError: (error) => set((state) => ({ pricingSalesPages: { ...state.pricingSalesPages, [key]: {
        ...(state.pricingSalesPages[key] || { query, items: [], nextCursor: null, totalCount: 0, hydrated: false }),
        loading: false, loadingMore: false, error,
      } } })),
      onFinish: () => undefined,
      errorMessage: "Unable to load pricing-version sales.",
    });
  },
});
